import React, { useEffect, useMemo, useRef, useState } from "react";
import posthog from "posthog-js";
import { TYPES } from "../constants/wineTypes";
import { OrderFormModal } from "./Carte/components/OrderFormModal";
import type { PublicOrderAccess } from "./Carte/components/orderForm.api";

interface Wine {
  id: string;
  type: string;
  name: string;
  mil: string;
  dom: string;
  prix: string;
  desc: string;
  visible: boolean;
  highlighted: boolean;
}

interface Brand {
  logo: string | null;
  bgColor: string;
  textColor: string;
  cardColor: string;
  feeder?: any;
}

interface CartePublicPageProps {
  wines: Wine[];
  maison: string;
  updated: string;
  brand: Brand;
  onBack?: () => void;
  publicOrderAccess?: PublicOrderAccess;
}

export function CartePublicPage({ wines, maison, updated, brand, onBack, publicOrderAccess }: CartePublicPageProps) {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState<string | null>(null);
  const [showOrder, setShowOrder] = useState(false);
  const [cart, setCart] = useState<Record<string, number>>({});
  const hasTrackedView = useRef(false);

  // Public view: hide invisible wines, bubble highlighted to the top of each section.
  const visibleSorted = useMemo(
    () =>
      [...wines]
        .filter((w) => w.visible !== false)
        .sort((a, b) => Number(b.highlighted) - Number(a.highlighted)),
    [wines]
  );

  const types = TYPES.filter((t) => visibleSorted.some((w) => w.type === t.id));
  const filtered = filter === "all" ? visibleSorted : visibleSorted.filter((w) => w.type === filter);
  const bg = brand.bgColor || "#0A0908";
  const txt = brand.textColor || "#F0EDE8";
  const card = brand.cardColor || "#141210";
  const cartItemsCount = visibleSorted.reduce((sum, wine) => sum + (cart[wine.id] || 0), 0);
  const cartTotal = visibleSorted.reduce((sum, wine) => sum + (parseFloat(wine.prix) || 0) * (cart[wine.id] || 0), 0);

  const trackCartePublicEvent = (eventName: string, payload: Record<string, unknown> = {}) => {
    if (!publicOrderAccess) {
      return;
    }

    posthog.capture(eventName, {
      maison,
      customer_hash: publicOrderAccess.customerHash,
      public_carte_owner_user_id: publicOrderAccess.userId || null,
      carte_id: publicOrderAccess.carteId || "default",
      visible_wine_count: visibleSorted.length,
      available_type_count: types.length,
      filter,
      access_mode: publicOrderAccess.userId ? "scoped" : "hash",
      has_custom_carte_id: Boolean(publicOrderAccess.carteId && publicOrderAccess.carteId !== "default"),
      ...payload,
    });
  };

  useEffect(() => {
    setCart((currentCart) => {
      const nextCart: Record<string, number> = {};
      for (const wine of visibleSorted) {
        nextCart[wine.id] = currentCart[wine.id] || 0;
      }
      return nextCart;
    });
  }, [visibleSorted]);

  useEffect(() => {
    if (!publicOrderAccess || hasTrackedView.current) {
      return;
    }

    hasTrackedView.current = true;
    trackCartePublicEvent("carte_public_viewed", {
      highlighted_wine_count: visibleSorted.filter((wine) => wine.highlighted).length,
    });
  }, [publicOrderAccess, visibleSorted]);

  const updateQty = (wineId: string, delta: number) => {
    const wine = visibleSorted.find((candidate) => candidate.id === wineId);

    setCart((currentCart) => ({
      ...currentCart,
      [wineId]: Math.max(0, (currentCart[wineId] || 0) + delta),
    }));

    const previousQuantity = cart[wineId] || 0;
    const nextQuantity = Math.max(0, previousQuantity + delta);
    if (!wine || nextQuantity === previousQuantity) {
      return;
    }

    trackCartePublicEvent(delta > 0 ? "carte_public_wine_added_to_cart" : "carte_public_wine_removed_from_cart", {
      wine_id: wine.id,
      wine_name: wine.name,
      wine_type: wine.type,
      previous_quantity: previousQuantity,
      next_quantity: nextQuantity,
    });
  };

  return (
    <main className="flex-1 overflow-auto" style={{ background: "#2A0E18" }}>
      {showOrder && (
        <OrderFormModal
          wines={visibleSorted}
          maison={maison}
          onClose={() => {
            trackCartePublicEvent("carte_public_order_modal_closed", {
              checkout_step: showOrder ? "modal_open" : "modal_closed",
              cart_item_count: cartItemsCount,
              cart_total: cartTotal,
            });
            setShowOrder(false);
          }}
          publicOrderAccess={publicOrderAccess}
          cart={cart}
          onUpdateQty={updateQty}
          onCartContinue={({ distinctWineCount, bottleCount, total }) => {
            trackCartePublicEvent("carte_public_checkout_started", {
              distinct_wine_count: distinctWineCount,
              bottle_count: bottleCount,
              order_value: total,
              currency: "EUR",
            });
          }}
          onBackToCart={({ distinctWineCount, bottleCount, total }) => {
            trackCartePublicEvent("carte_public_checkout_returned_to_cart", {
              distinct_wine_count: distinctWineCount,
              bottle_count: bottleCount,
              order_value: total,
              currency: "EUR",
            });
          }}
          onOrderSubmitted={({ distinctWineCount, bottleCount, total }) => {
            trackCartePublicEvent("carte_public_order_submitted", {
              distinct_wine_count: distinctWineCount,
              bottle_count: bottleCount,
              order_value: total,
              currency: "EUR",
            });
          }}
          onOrderSubmitFailed={({ distinctWineCount, bottleCount, total, message }) => {
            trackCartePublicEvent("carte_public_order_submission_failed", {
              distinct_wine_count: distinctWineCount,
              bottle_count: bottleCount,
              order_value: total,
              currency: "EUR",
              error_message: message,
            });
          }}
        />
      )}
      <div className="relative overflow-hidden px-5 pb-16 pt-16 text-center" style={{ background: bg, color: txt }}>
        <div className="absolute -left-12 -top-16 h-[280px] w-[280px] rounded-full bg-white/10" />
        <div className="absolute -bottom-20 -right-10 h-[240px] w-[240px] rounded-full bg-[#7A2438]/40" />
        <div className="relative mx-auto max-w-[720px]">
          {onBack && (
            <button onClick={onBack} className="absolute left-0 top-0 rounded-[12px] border border-white/30 bg-white/10 px-4 py-2 text-[13px] font-semibold text-[#FFF6EE] backdrop-blur">
              ← Retour
            </button>
          )}
          <div className="text-[13px] uppercase tracking-[0.35em] text-[#FFF6EE]/80">Carte des vins</div>
          {brand.logo && <img src={brand.logo} alt="" className="mx-auto mt-5 h-12 w-12 rounded-xl object-contain" />}
          <h1 className="mt-4 font-display text-[44px] font-bold tracking-[-0.06em] text-[#FFF6EE]">{maison || "Ma Cave"}</h1>
          <div className="mt-3 text-[15px] text-[#FFF6EE]/85">{visibleSorted.length} vins · mis à jour {updated}</div>
        </div>
      </div>
      <div className="mx-auto -mt-10 max-w-[720px] px-4 pb-20">
        <div className="mb-4 flex flex-wrap gap-2 rounded-[22px] border border-white/5 bg-[rgba(255,252,246,0.06)] p-3 backdrop-blur">
          {[{ id: "all", label: `Tous (${visibleSorted.length})` }, ...types.map((t) => ({ id: t.id, label: `${t.label} (${visibleSorted.filter((w) => w.type === t.id).length})` }))].map((f) => (
            <button key={f.id} onClick={() => {
              if (f.id !== filter) {
                trackCartePublicEvent("carte_public_filter_selected", {
                  previous_filter: filter,
                  next_filter: f.id,
                  result_count: f.id === "all" ? visibleSorted.length : visibleSorted.filter((w) => w.type === f.id).length,
                });
              }
              setFilter(f.id);
            }} aria-pressed={filter === f.id}
              className={`min-h-[40px] rounded-full border px-4 text-[13px] font-semibold transition-all ${filter === f.id ? "border-accent-border bg-accent text-[#FFF6EE]" : "border-white/10 bg-transparent text-[#FFF6EE]/80"}`}>{f.label}</button>
          ))}
        </div>

        {types.filter((t) => filtered.some((w) => w.type === t.id)).map((type) => (
          <section key={type.id} className="mb-5">
            <div className="mb-3 flex items-center gap-3 px-2">
              <div className="h-[3px] w-[18px] rounded-full" style={{ background: type.color }} />
              <h2 className="font-display text-[22px] font-bold tracking-[-0.03em]" style={{ color: type.color }}>{type.label}</h2>
            </div>
            <div className="space-y-3">
              {filtered.filter((w) => w.type === type.id).map((w) => (
                <article key={w.id} className="rounded-[24px] bg-[#FFFCF6] px-6 py-5 shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
                  <button onClick={() => {
                    const nextOpen = open === w.id ? null : w.id;
                    trackCartePublicEvent(nextOpen === w.id ? "carte_public_wine_details_opened" : "carte_public_wine_details_closed", {
                      wine_id: w.id,
                      wine_name: w.name,
                      wine_type: w.type,
                    });
                    setOpen(nextOpen);
                  }} aria-expanded={open === w.id}
                    className="w-full border-none bg-transparent text-left">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[18px] font-semibold text-[#3A1420]">
                          {w.highlighted && <span className="text-[#CC9A3E]">★</span>}
                          <span>{w.name}</span>
                        </div>
                        <div className="mt-1 text-[14px] text-[#61474D]">{w.dom} · {w.mil}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[18px] font-bold" style={{ color: type.color }}>{w.prix} €</div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#61474D]">{type.short}</div>
                      </div>
                    </div>
                  </button>
                  <div className="mt-4 flex items-center justify-between gap-4 border-t border-[#3A1420]/8 pt-4">
                    <div className="text-[12px] uppercase tracking-[0.16em] text-[#61474D]">Panier</div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateQty(w.id, -1)}
                        className={`flex h-10 w-10 items-center justify-center rounded-[12px] border text-[20px] font-medium transition-colors ${
                          cart[w.id] > 0
                            ? "border-[#7A2438]/20 bg-[#7A2438]/6 text-[#7A2438]"
                            : "border-[#3A1420]/10 bg-transparent text-[#9B8B90]"
                        }`}
                        aria-label={`Retirer une bouteille de ${w.name}`}
                      >
                        −
                      </button>
                      <div className="min-w-[32px] text-center text-[16px] font-semibold text-[#3A1420]">{cart[w.id] || 0}</div>
                      <button
                        type="button"
                        onClick={() => updateQty(w.id, 1)}
                        className="flex h-10 w-10 items-center justify-center rounded-[12px] border border-accent/20 bg-accent text-[20px] font-medium text-[#FFF6EE] shadow-[0_10px_24px_rgba(217,102,60,0.2)] transition-transform active:scale-[0.98]"
                        aria-label={`Ajouter une bouteille de ${w.name}`}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {open === w.id && (
                    <div className="mt-4 border-t border-[#3A1420]/8 pt-4 text-[14px] leading-7 text-[#61474D]">
                      {w.desc || "Description bientôt disponible."}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        ))}

        <div className="sticky bottom-0 mt-6 flex items-center justify-between gap-3 rounded-[18px] bg-[#2A0E18]/88 px-4 py-3 backdrop-blur">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFF6EE]/35">Propulsé par vinIA</div>
            <div className="mt-1 text-[13px] text-[#FFF6EE]/80">{cartItemsCount} bouteilles · {cartTotal.toFixed(2)} €</div>
          </div>
          <button onClick={() => {
            trackCartePublicEvent("carte_public_order_modal_opened", {
              cart_item_count: cartItemsCount,
              cart_total: cartTotal,
            });
            setShowOrder(true);
          }}
            disabled={cartItemsCount === 0}
            className="min-h-[44px] rounded-[14px] bg-accent px-5 text-[14px] font-semibold text-[#FFF6EE] shadow-[0_10px_24px_rgba(217,102,60,0.26)] disabled:cursor-not-allowed disabled:opacity-50">
            Commander
          </button>
          </div>
      </div>
    </main>
  );
}
